import { useCallback, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { dealApi, dealQueryKeys } from '@crm/entities/deal';
import { toastManager } from '@crm/entities/toast';
import { getApiErrorMessage } from '@crm/shared/api';
import { DateHelpers } from '@crm/shared/lib';
import { tasksInformationActions } from '@entities/task';
import { useAppDispatch, useAppSelector } from 'store';
import { getUpdatedFieldErrors } from 'widgets/Modals/lib/getUpdatedFieldErrors';
import {
    changeIsCompleted,
    changeModalDescription,
    changeModalTitle,
    resetModalData,
} from 'widgets/Modals/model/slice/addModalSlice';

import { mapDealTasksDataToTasksInformation } from './mapDealTasksDataToTasksInformation';

import type { TDeal, TDealTasksData } from '@crm/entities/deal';
import type { IModalAttachment } from '@entities/attachment';
import type { FieldErrors, IAddModalSchemaState } from '@entities/modal';
import type { TAPIError } from 'widgets/Modals/model/types/apiError.type';

const getInitialFieldErrors = (): FieldErrors => ({
    title: { isError: false, errorMessage: null },
    description: { isError: false, errorMessage: null },
    objectOrEstimate: { isError: false, errorMessage: null },
    executor: { isError: false, errorMessage: null },
    tags: { isError: false, errorMessage: null },
    date: { isError: false, errorMessage: null },
});

const formatTaskDate = (date: string | null) => {
    if (!date || date.length === 1) {
        return '';
    }

    const parsedDate = DateHelpers.parseDisplayDate(date);

    return parsedDate ? DateHelpers.formatApiDate(parsedDate) : '';
};

const appendNullableNumber = (formData: FormData, name: string, value: number | '' | null) => {
    if (value !== '' && value !== null) {
        formData.append(name, String(value));
    }
};

const buildCreateDealTaskFormData = ({
    attachments,
    color,
    date,
    description,
    estimateId,
    executorId,
    isCompleted,
    observerIds,
    projectId,
    status,
    tags,
    title,
}: IAddModalSchemaState & {
    attachments: IModalAttachment[];
}) => {
    const formData = new FormData();

    formData.append('title', title.trim());
    formData.append('description', description);
    formData.append('color', color);
    formData.append('date', formatTaskDate(date));
    formData.append('is_completed', isCompleted ? '1' : '0');
    formData.append('status', status);

    tags.forEach((tag) => {
        formData.append('tags[]', tag);
    });

    observerIds.forEach((observerId) => {
        formData.append('observer_ids[]', String(observerId));
    });

    attachments.forEach((attachment) => {
        formData.append('new_attachments[]', attachment.file);
    });

    appendNullableNumber(formData, 'executor_id', executorId);
    appendNullableNumber(formData, 'project_id', projectId);
    appendNullableNumber(formData, 'estimate_id', estimateId);

    return formData;
};

export const useCreateDealTaskModal = ({
    dealId,
    tasksData,
}: {
    dealId: TDeal['id'];
    tasksData?: TDealTasksData;
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const modalState = useAppSelector((state) => state.addModalReducer);
    const { isModalLoading } = useAppSelector((state) => state.tasksReducer);
    const [isOpen, setIsOpen] = useState(false);
    const [fileIdToPreview, setFileIdToPreview] = useState<string | null>(null);
    const [fileIdToDeleteFromList, setFileIdToDeleteFromList] = useState<string | null>(null);
    const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
    const [previewEnable, setPreviewEnable] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>(getInitialFieldErrors);
    const [attachments, setAttachments] = useState<IModalAttachment[]>([]);
    const [triggerAnimationError, setTriggerAnimationError] = useState(false);
    const [isCheckedRadio, setIsCheckedRadio] = useState(false);

    const resetModal = useCallback(() => {
        setFieldErrors(getInitialFieldErrors());
        setAttachments([]);
        setPreviewEnable(false);
        setFileIdToPreview(null);
        setFileIdToDeleteFromList(null);
        setDeleteConfirmModal(false);
        setTriggerAnimationError(false);
        setIsCheckedRadio(false);
        dispatch(resetModalData());
    }, [dispatch]);

    const createDealTaskMutation = useMutation({
        mutationKey: [...dealQueryKeys.detail(dealId), 'tasks', 'create'],
        mutationFn: () =>
            dealApi.createDealTask({
                dealId,
                payload: buildCreateDealTaskFormData({
                    ...modalState,
                    attachments,
                }),
            }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: dealQueryKeys.detail(dealId) });
            toastManager.onSuccess({
                title: t('crm.dealDetailsContent.tasksBlock.notifications.createSuccess'),
            });
            setIsOpen(false);
            resetModal();
        },
        onError: (error) => {
            toastManager.onError({
                title: getApiErrorMessage(
                    error,
                    t('crm.dealDetailsContent.tasksBlock.errors.create')
                ),
            });
        },
    });

    const onOpenModal = () => {
        dispatch(
            tasksInformationActions.setTasksInformation(
                mapDealTasksDataToTasksInformation(tasksData)
            )
        );
        setIsOpen(true);
    };

    const onHideModal = () => {
        setIsOpen(false);
        resetModal();
    };

    const deleteAttachment = (fileId: string | undefined) => {
        if (!fileId) return;
        setAttachments((prev) => prev.filter((attachment) => attachment.id !== fileId));
        setFileIdToDeleteFromList(null);
        setDeleteConfirmModal(false);
    };

    const onHidePrevModal = useCallback(() => {
        setPreviewEnable(false);
        setFileIdToPreview(null);
    }, []);

    const onEnablePreviewModal = useCallback((enablePreview: boolean, fileId: string) => {
        setPreviewEnable(enablePreview);
        setFileIdToPreview(fileId);
    }, []);

    const handleRadioBtn = () => {
        if (modalState.title.length === 0) {
            return;
        }

        const nextValue = !isCheckedRadio;
        setIsCheckedRadio(nextValue);
        dispatch(changeIsCompleted(nextValue));
    };

    const onChangeModalTitle = (title: string) => {
        if (fieldErrors.title.isError) {
            setFieldErrors((prev) => ({
                ...prev,
                title: { isError: false, errorMessage: null },
            }));
        }

        dispatch(changeModalTitle(title));
    };

    const onChangeModalDescription = (description: string) => {
        if (fieldErrors.description.isError) {
            setFieldErrors((prev) => ({
                ...prev,
                description: { isError: false, errorMessage: null },
            }));
        }

        dispatch(changeModalDescription(description));
    };

    const onSubmit = async () => {
        if (modalState.title.trim().length === 0) {
            setFieldErrors((prev) => ({
                ...prev,
                title: {
                    isError: true,
                    errorMessage: t('crm.dealDetailsContent.tasksBlock.validation.titleRequired'),
                },
            }));
            setTriggerAnimationError(true);
            return;
        }

        if (
            fieldErrors.date.isError ||
            fieldErrors.tags.isError ||
            fieldErrors.title.isError ||
            fieldErrors.description.isError ||
            fieldErrors.executor.isError ||
            fieldErrors.objectOrEstimate.isError
        ) {
            return;
        }

        try {
            setFieldErrors(getInitialFieldErrors());
            await createDealTaskMutation.mutateAsync();
        } catch (error) {
            if (typeof error === 'object' && error !== null && 'errors' in error) {
                const apiErrors = error.errors as TAPIError[];
                const updatedFieldErrors = getUpdatedFieldErrors(fieldErrors, apiErrors);
                setFieldErrors(updatedFieldErrors);
            }
        }
    };

    return {
        attachments,
        deleteAttachment,
        deleteConfirmModal,
        fieldErrors,
        fileIdToDeleteFromList,
        fileIdToPreview,
        handleRadioBtn,
        isCheckedRadio,
        isModalLoading: isModalLoading || createDealTaskMutation.isPending,
        modalMode: isOpen ? ('addNewTask' as const) : null,
        modalState,
        onChangeModalDescription,
        onChangeModalTitle,
        onEnablePreviewModal,
        onHideModal,
        onHidePrevModal,
        onOpenModal,
        onSubmit,
        previewEnable,
        setAttachments,
        setDeleteConfirmModal,
        setFieldErrors,
        setFileIdToDeleteFromList,
        setTriggerAnimationError,
        triggerAnimationError,
    };
};
